import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  Card, 
  CardContent, 
  Chip,
  Stack,
  IconButton,
  InputAdornment,
  Divider,
  Fade,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { 
  CreditCard as CreditCardIcon, 
  Person, 
  Email, 
  Phone, 
  ArrowBack,
  CheckCircle,
  Wifi,
  Lock,
  Security,
  Close,
  Info,
  OpenInNew,
  ReceiptLong,
} from '@mui/icons-material';
import { useAuth } from '../../../hooks/useAuth';
import { asaasService } from '../../../services/asaasService';
import { apiClientBff } from '../../../services/apiBff';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { Loading } from '../../../components/ui/Loading';
import { onboardingService } from '../services/onboardingService';
import { isValidCPF } from '../../../services/userUtils';
import { showError, toast } from '../../../utils/errorHandler';
import {
  DEFAULT_PAID_PLAN,
  PaidPlanType,
  normalizePaidPlanType,
} from '../services/onboardingService';

const defaultRemoteIp = '187.100.10.10';
const PLAN_LABEL_PT: Record<PaidPlanType, string> = {
  producer: 'Produtor',
  professional: 'Profissional',
};

export type AsaasPaymentScreenVariant = 'default' | 'webview';

interface AsaasPaymentScreenProps {
  variant?: AsaasPaymentScreenVariant;
}

type PaymentMethod = 'CREDIT_CARD' | 'BOLETO';

interface BoletoPaymentDetails {
  invoiceUrl: string;
  dueDate?: string | null;
  value?: number | null;
}

interface OnboardingTrialCouponPayload {
  code: string;
  discountPercentage: number;
  expirationDate?: string | null;
  durationMonths?: number;
  status?: string | null;
  usageDate?: string | null;
  userEmail?: string | null;
}

type OnboardingTrialStatus = 'idle' | 'created' | 'existing_active' | 'eligible_to_create' | 'already_consumed' | 'not_found';

const formatCpfCnpj = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  } else {
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  }
};

const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1');
};

const formatCep = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{3})\d+?$/, '$1');
};

const normalizePaymentsList = (raw: any): any[] => {
  if (raw?.data?.data && Array.isArray(raw.data.data)) return raw.data.data;
  if (raw?.data?.items && Array.isArray(raw.data.items)) return raw.data.items;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  return [];
};

const getBoletoInvoiceUrl = (payment: any): string => {
  const directUrl = String(payment?.invoiceUrl || '').trim();
  if (directUrl) return directUrl;
  const bankSlipUrl = String(payment?.bankSlipUrl || '').trim();
  if (bankSlipUrl) return bankSlipUrl;
  const nestedBankSlipUrl = String(payment?.bankSlip?.url || '').trim();
  if (nestedBankSlipUrl) return nestedBankSlipUrl;
  return '';
};

const getCouponInfoFromPayment = (payment: any): { code: string; discount: number; expirationDate?: string } | null => {
  const description = String(payment?.description || '').trim();
  if (!description) return null;

  const percentMetaMatch = description.match(/\[MUU_PERCENT_COUPON\|code=([^|]+)\|base=([^|]+)\|until=([^\]]+)\]/);
  if (percentMetaMatch) {
    const code = decodeURIComponent(String(percentMetaMatch[1] || '')).trim();
    const baseValue = Number(percentMetaMatch[2] || '');
    const effectiveExpiryIso = decodeURIComponent(String(percentMetaMatch[3] || '')).trim();
    const paymentValue = Number(payment?.value);
    if (code && Number.isFinite(baseValue) && baseValue > 0 && Number.isFinite(paymentValue) && paymentValue >= 0) {
      const rawDiscount = ((baseValue - paymentValue) / baseValue) * 100;
      const discount = Math.max(0, Math.min(100, Math.round(rawDiscount * 100) / 100));
      if (discount > 0) {
        return { code, discount, expirationDate: effectiveExpiryIso || undefined };
      }
    }
  }

  const couponLabelMatch = description.match(/\(Coupon:\s*([^)]+?)\s*-\s*(\d+(?:[.,]\d+)?)%\s*OFF\)/i);
  if (!couponLabelMatch) return null;
  const code = String(couponLabelMatch[1] || '').trim();
  const discount = Number(String(couponLabelMatch[2] || '').replace(',', '.'));
  if (!code || !Number.isFinite(discount) || discount <= 0) return null;
  return { code, discount };
};

const delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));
const DEFAULT_PLAN_PRICE_VALUE = 30.0;

const AsaasPaymentScreen: React.FC<AsaasPaymentScreenProps> = ({ variant = 'default' }) => {
  const { t, i18n } = useTranslation(['common', 'landing']);
  const locale = String(t('locale', { defaultValue: i18n.language }));
  const navigate = useNavigate();
  const location = useLocation();
  const { authState, refreshPlanStatus, logout, login } = useAuth();
  const locationState = (location.state as any) || {};
  const storedTrialBootstrapState = onboardingService.getOnboardingTrialBootstrapState();
  const registrationToken = String(locationState?.registrationToken || storedTrialBootstrapState?.token || '').trim();
  const registrationEmail = String(locationState?.registrationEmail || locationState?.email || storedTrialBootstrapState?.email || '').trim().toLowerCase();
  const registrationPassword = String(locationState?.registrationPassword || locationState?.password || '').trim();
  const registrationJustCreated = Boolean(locationState?.registrationJustCreated);

  const params = new URLSearchParams(location.search);
  const planFromUrl = normalizePaidPlanType(params.get('plan')) || DEFAULT_PAID_PLAN;
  const [selectedPlan, setSelectedPlan] = useState<PaidPlanType>(planFromUrl);

  const initialEmail = useMemo(() => registrationEmail || authState.user?.email || params.get('email') || '', [registrationEmail, authState.user, params]);

  const isEmailFixed = !!registrationEmail || !!authState.user?.email || !!params.get('email');

  const planLabel = useMemo(() => {
    const planKey: PaidPlanType = selectedPlan || DEFAULT_PAID_PLAN;
    return t(`landing:pricing.plans.${planKey}.name`);
  }, [selectedPlan, t]);

  const [name, setName] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [email, setEmail] = useState(initialEmail);
  const [mobilePhone, setMobilePhone] = useState('');
  
  const [cardHolderName, setCardHolderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [ccv, setCcv] = useState('');
  
  const [postalCode, setPostalCode] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [addressComplement, setAddressComplement] = useState('');
  const [addressStreet, setAddressStreet] = useState('');
  const [addressDistrict, setAddressDistrict] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [phone, setPhone] = useState('');

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);
  const lastCepFetchedRef = useRef<string>('');
  const [showPostSubscriptionSuccess, setShowPostSubscriptionSuccess] = useState(false);
  const bypassNextNavigationRef = useRef(false);

  const [planPrice, setPlanPrice] = useState('30,00');
  const [planPriceValue, setPlanPriceValue] = useState(DEFAULT_PLAN_PRICE_VALUE);
  const [couponInfo, setCouponInfo] = useState<{ code: string; discount: number; expirationDate?: string } | null>(null);
  const [couponCodeInput, setCouponCodeInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponAppliedForEmail, setCouponAppliedForEmail] = useState('');
  const [autoTrialStatus, setAutoTrialStatus] = useState<OnboardingTrialStatus>('idle');
  const [autoTrialLoading, setAutoTrialLoading] = useState(false);
  const [autoTrialError, setAutoTrialError] = useState<string | null>(null);
  const [autoTrialCouponCode, setAutoTrialCouponCode] = useState('');
  const [autoTrialRetryNonce, setAutoTrialRetryNonce] = useState(0);
  const [autoTrialCreationRequired, setAutoTrialCreationRequired] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CREDIT_CARD');
  const [boletoDetails, setBoletoDetails] = useState<BoletoPaymentDetails | null>(null);
  const [hasResumedPendingBoleto, setHasResumedPendingBoleto] = useState(false);

  const getAccessCheckOptions = useCallback(() => {
    const token = String(registrationToken || '').trim();
    return token ? { token } : undefined;
  }, [registrationToken]);
  
  useEffect(() => {
    if (planFromUrl !== selectedPlan) {
      setSelectedPlan(planFromUrl);
    }
  }, [planFromUrl, selectedPlan]);

  useEffect(() => {
    const normalizedEmail = String(registrationEmail || '').trim().toLowerCase();
    if (!registrationJustCreated || !normalizedEmail) return;
    const currentBootstrapState = onboardingService.getOnboardingTrialBootstrapState();
    const alreadyStored =
      String(currentBootstrapState?.email || '').trim().toLowerCase() === normalizedEmail
      && Boolean(currentBootstrapState?.shouldCreate)
      && String(currentBootstrapState?.token || '').trim() === String(registrationToken || '').trim();
    if (alreadyStored) return;
    onboardingService.storeOnboardingTrialBootstrapState({
      email: normalizedEmail,
      shouldCreate: true,
      token: registrationToken || undefined,
    });
    setAutoTrialRetryNonce((current) => current + 1);
  }, [registrationEmail, registrationJustCreated, registrationToken]);

  const handlePlanChange = (event: SelectChangeEvent<PaidPlanType>) => {
    const nextPlan = event.target.value as PaidPlanType;
    setSelectedPlan(nextPlan);
    onboardingService.storePlanSelection(nextPlan);
    const nextParams = new URLSearchParams(location.search);
    nextParams.set('plan', nextPlan);
    navigate({ pathname: location.pathname, search: `?${nextParams.toString()}` }, { replace: true, state: location.state });
  };

  const handleBackToPlanSelection = () => {
    const isWebview = location.pathname.startsWith('/onboarding-webview');
    const planSelectionPath = isWebview ? '/onboarding-webview/plan-selection' : '/plan-selection';
    const nextParams = new URLSearchParams();
    if (selectedPlan) nextParams.set('plan', selectedPlan);
    if (email) nextParams.set('email', email);
    const search = nextParams.toString();
    navigate(`${planSelectionPath}${search ? `?${search}` : ''}`, { state: location.state });
  };

  const handleBackNavigation = () => {
    if (activeStep === 1) {
      setActiveStep(0);
      return;
    }
    handleBackToPlanSelection();
  };

  const checkSubscription = async (checkEmail: string) => {
    if (!checkEmail || !checkEmail.includes('@')) return;
    const normalizedEmail = checkEmail.trim().toLowerCase();
    if (!normalizedEmail || !normalizedEmail.includes('@')) return;
    try {
      const access = await asaasService.checkAccessByEmail(normalizedEmail, getAccessCheckOptions());
      const reasons = Array.isArray((access as any)?.reasons) ? (access as any).reasons : [];
      const isCouponGranted = reasons.includes('coupon_granted');
      const hasValidSubscription = reasons.includes('subscription_valid');
      const granted = !isCouponGranted && Boolean((access as any)?.accessGranted);
      const existingSub = !isCouponGranted && hasValidSubscription ? (access as any)?.subscription : null;
      
      if (existingSub?.id && !granted) {
        const paymentsResp = await asaasService.listSubscriptionPayments(String(existingSub.id));
        const paymentItems = normalizePaymentsList(paymentsResp);
        const pendingBoletoPayment =
          paymentItems.find((p: any) => String(p?.billingType || '').toUpperCase() === 'BOLETO' && String(p?.status || '').toUpperCase() === 'PENDING') ||
          paymentItems.find((p: any) => String(p?.billingType || '').toUpperCase() === 'BOLETO') ||
          null;
        const invoiceUrl = getBoletoInvoiceUrl(pendingBoletoPayment);
        if (invoiceUrl) {
          const resumedCoupon = getCouponInfoFromPayment(pendingBoletoPayment);
          const resumedValue = Number(pendingBoletoPayment?.value);
          if (resumedCoupon) {
            setCouponInfo(resumedCoupon);
            setCouponAppliedForEmail(normalizedEmail);
            setCouponCodeInput(resumedCoupon.code);
          } else {
            setCouponInfo(null);
            setCouponAppliedForEmail('');
            setCouponCodeInput('');
          }
          if (Number.isFinite(resumedValue) && resumedValue >= 0) {
            setPlanPriceValue(resumedValue);
            setPlanPrice(resumedValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
          }
          setPaymentMethod('BOLETO');
          setBoletoDetails({
            invoiceUrl,
            dueDate: pendingBoletoPayment?.dueDate || null,
            value: typeof pendingBoletoPayment?.value === 'number' ? pendingBoletoPayment.value : null,
          });
          setHasResumedPendingBoleto(true);
          setShowPostSubscriptionSuccess(true);
          setMessage(null);
          return;
        }
      }

      if (granted || existingSub) {
        setMessage(t('payment.error_active_subscription'));
        if (!authState.isAuthenticated && variant !== 'webview') {
           setTimeout(() => {
             bypassNextNavigationRef.current = true;
             navigate('/auth?signup=false', { replace: true });
           }, 3000);
        }
        return;
      } else {
        if (message?.includes('assinatura ativa')) setMessage(null);
      }
    } catch (e) {
      Sentry.setTag('onboarding_step', 'check_subscription');
      Sentry.setTag('bff_status', String((e as any)?.status || 0));
      Sentry.setExtra('bff_error', (e as any)?.data?.message || String(e));
      if (initialEmail) Sentry.setUser({ email: initialEmail });
      Sentry.captureException(e as Error);
    }
  };

  useEffect(() => {
    if (variant === 'webview') return;
    if (initialEmail) checkSubscription(initialEmail);
  }, [initialEmail, variant]);

  useEffect(() => {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!couponInfo) return;
    if (!couponAppliedForEmail) return;
    if (!normalizedEmail || !normalizedEmail.includes('@')) return;
    if (normalizedEmail === couponAppliedForEmail) return;

    setCouponInfo(null);
    setCouponAppliedForEmail('');
    setPlanPriceValue(30.0);
    setPlanPrice('30,00');
    setCouponError(null);
  }, [email, couponAppliedForEmail, couponInfo]);

  const isAutoTrialActive = autoTrialStatus === 'created' || autoTrialStatus === 'existing_active';
  const shouldDisplayAutoTrial = isAutoTrialActive || autoTrialStatus === 'eligible_to_create';
  const shouldApplyAutoTrialOnSubmit = shouldDisplayAutoTrial;

  useEffect(() => {
    if (couponInfo?.discount === 100 && !shouldDisplayAutoTrial) {
      setPaymentMethod('CREDIT_CARD');
    }
  }, [couponInfo, shouldDisplayAutoTrial]);

  const isFullDiscountCoupon = Number(couponInfo?.discount || 0) === 100;
  const isCouponInputLocked = shouldDisplayAutoTrial;
  const isAutoTrialBlocking = autoTrialCreationRequired && !shouldDisplayAutoTrial;
  const autoTrialEndsAt = shouldDisplayAutoTrial ? String(couponInfo?.expirationDate || '').trim() : '';
  const autoTrialDisplayDate = autoTrialEndsAt ? new Date(autoTrialEndsAt).toLocaleDateString(locale) : '';
  const autoTrialSummaryPrice = DEFAULT_PLAN_PRICE_VALUE.toLocaleString(locale, {
    style: 'currency',
    currency: 'BRL',
  });

  const applyAutoTrialCoupon = useCallback((coupon: OnboardingTrialCouponPayload, normalizedEmail: string) => {
    const code = String(coupon?.code || '').trim();
    const expirationDate = String(coupon?.expirationDate || '').trim();
    if (!code) return;
    setAutoTrialCouponCode(code);
    setCouponInfo({
      code,
      discount: Number(coupon?.discountPercentage || 100),
      expirationDate: expirationDate || undefined,
    });
    setCouponAppliedForEmail(normalizedEmail);
    setCouponCodeInput(code);
    setCouponError(null);
    setPlanPriceValue(0);
    setPlanPrice('0,00');
  }, []);

  useEffect(() => {
    const normalizedEmail = String(registrationEmail || initialEmail || email || '').trim().toLowerCase();
    if (!normalizedEmail || !normalizedEmail.includes('@')) return;

    const bootstrapState = onboardingService.getOnboardingTrialBootstrapState();
    const shouldUseStoredState = String(bootstrapState?.email || '').trim().toLowerCase() === normalizedEmail;
    if (!shouldUseStoredState) return;

    const allowCreate = Boolean(bootstrapState?.shouldCreate);
    const effectiveToken = String(registrationToken || bootstrapState?.token || '').trim();
    setAutoTrialCreationRequired(allowCreate);

    if (!effectiveToken && !authState.isAuthenticated) {
      if (allowCreate) {
        setAutoTrialError(t('payment.auto_trial_missing_token'));
      }
      return;
    }

    let cancelled = false;
    setAutoTrialLoading(true);
    setAutoTrialError(null);

    (async () => {
      try {
        const response: any = await asaasService.bootstrapOnboardingTrial(
          { email: normalizedEmail, allowCreate },
          { token: effectiveToken || undefined },
        );
        if (cancelled) return;

        const responseStatus = String(response?.status || 'idle') as OnboardingTrialStatus;
        const coupon = (response?.coupon || null) as OnboardingTrialCouponPayload | null;
        setAutoTrialStatus(responseStatus);

        if ((responseStatus === 'created' || responseStatus === 'existing_active' || responseStatus === 'eligible_to_create') && coupon) {
          applyAutoTrialCoupon(coupon, normalizedEmail);
          if (responseStatus === 'eligible_to_create') {
            onboardingService.storeOnboardingTrialBootstrapState({
              email: normalizedEmail,
              shouldCreate: allowCreate,
              token: effectiveToken || undefined,
            });
            setAutoTrialCreationRequired(allowCreate);
          } else {
            onboardingService.storeOnboardingTrialBootstrapState({
              email: normalizedEmail,
              shouldCreate: false,
              token: effectiveToken || undefined,
            });
            setAutoTrialCreationRequired(false);
          }
          return;
        }

        if (responseStatus === 'already_consumed' || responseStatus === 'not_found') {
          if (couponInfo?.code && couponInfo.code === (coupon?.code || autoTrialCouponCode)) {
            setCouponInfo(null);
            setCouponAppliedForEmail('');
            setCouponCodeInput('');
            setPlanPriceValue(DEFAULT_PLAN_PRICE_VALUE);
            setPlanPrice('30,00');
          }
          setAutoTrialCouponCode('');
          onboardingService.clearOnboardingTrialBootstrapState();
          setAutoTrialCreationRequired(false);
        }
      } catch (error: any) {
        if (cancelled) return;
        const dataMessage = String(error?.data?.message || '').trim();
        const status = Number(error?.status || 0);
        const message = dataMessage
          || (status === 401 ? t('payment.auto_trial_missing_token') : t('payment.auto_trial_failed'));
        setAutoTrialError(message);
        setAutoTrialStatus('idle');
      } finally {
        if (!cancelled) setAutoTrialLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [registrationEmail, initialEmail, email, registrationToken, authState.isAuthenticated, autoTrialRetryNonce, applyAutoTrialCoupon, t]);

  const getCouponErrorMessage = (e: any) => {
    const dataMessage = e?.data?.message;
    if (dataMessage) return String(dataMessage);

    const status = Number(e?.status || 0);
    if (status === 404) return 'Cupom não encontrado';
    if (status === 401) return 'Você não tem permissão para validar este cupom';

    const msg = String(e?.message || '').trim();
    if (msg.startsWith('HTTP error! status:')) return 'Erro ao validar cupom';
    return msg || 'Erro ao validar cupom';
  };

  const getSubscriptionErrorMessage = (e: any) => {
    const dataMessage = String(
      e?.data?.message ||
      e?.data?.error ||
      e?.data?.errors?.[0]?.description ||
      e?.data?.errors?.[0]?.message ||
      ''
    ).trim();
    if (dataMessage) return dataMessage;

    const msg = String(e?.message || '').trim();
    if (msg && !msg.startsWith('HTTP error! status:')) {
      return msg;
    }

    const status = Number(e?.status || 0);
    if (status === 402) return t('payment.card_not_authorized');
    if (status === 422) return t('payment.payment_data_invalid');
    if (status === 401 || status === 403) return t('payment.payment_not_allowed');
    if (status >= 500) return t('payment.payment_service_unavailable');
    if (status === 0) return t('payment.network_error');
    if (msg.startsWith('HTTP error! status:')) {
      return t('payment.subscription_creation_failed');
    }
    return msg || t('payment.subscription_creation_failed');
  };

  const handleCouponInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isCouponInputLocked) return;
    setCouponCodeInput(e.target.value);
    if (couponError) setCouponError(null);
  };

  const clearCoupon = () => {
    if (isCouponInputLocked) return;
    setCouponInfo(null);
    setCouponAppliedForEmail('');
    setCouponCodeInput('');
    setCouponError(null);
    setMessage(null);
    setPlanPriceValue(DEFAULT_PLAN_PRICE_VALUE);
    setPlanPrice('30,00');
  };

  const applyCouponCode = async () => {
    if (isCouponInputLocked) return;
    setMessage(null);
    setCouponError(null);

    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      const msg = t('payment.fill_all_fields');
      setCouponError(msg);
      showError(msg);
      return;
    }

    const code = String(couponCodeInput || '').trim();
    if (!code) return;

    setCouponLoading(true);
    try {
      const resp: any = await asaasService.validateCouponByCode(
        { couponCode: code, email: normalizedEmail },
        { token: registrationToken || undefined },
      );
      if (!resp?.valid) {
        setCouponInfo(null);
        setPlanPriceValue(DEFAULT_PLAN_PRICE_VALUE);
        setPlanPrice('30,00');
        const msg = resp?.message || 'Cupom inválido';
        setCouponError(msg);
        showError(msg);
        return;
      }

      const discount = Number(resp.discountPercentage ?? 0);
      const couponCode = String(resp.code || code).trim();
      const expirationDate = resp?.expirationDate ? String(resp.expirationDate) : undefined;
      const originalPrice = 30.0;
      const newPrice = Math.max(0, originalPrice * (1 - discount / 100));

      setCouponInfo({ code: couponCode, discount, expirationDate });
      setCouponAppliedForEmail(normalizedEmail);
      setPlanPriceValue(newPrice);
      setPlanPrice(newPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    } catch (e: any) {
      Sentry.setTag('onboarding_step', 'apply_coupon');
      Sentry.setTag('coupon_code', couponCodeInput);
      Sentry.setTag('bff_status', String(e?.status || 0));
      Sentry.setExtra('bff_error', e?.data?.message || e?.message);
      Sentry.setExtra('has_registration_token', !!registrationToken);
      Sentry.captureException(e);

      setCouponInfo(null);
      setPlanPriceValue(DEFAULT_PLAN_PRICE_VALUE);
      setPlanPrice('30,00');
      const msg = getCouponErrorMessage(e);
      setCouponError(msg);
      showError(e); // Dynamically extract
    } finally {
      setCouponLoading(false);
    }
  };

  const retryAutoTrialBootstrap = () => {
    setAutoTrialRetryNonce((current) => current + 1);
  };

  const renderAutoTrialChip = (size: 'small' | 'medium' = 'small') => (
    <Chip
      size={size}
      label={t('payment.auto_trial_badge')}
      sx={{
        height: size === 'small' ? 28 : 32,
        borderRadius: 999,
        bgcolor: `${DESIGN_TOKENS.colors.status.success[500]}14`,
        color: DESIGN_TOKENS.colors.status.success[500],
        border: `1px solid ${DESIGN_TOKENS.colors.status.success[500]}40`,
        fontWeight: 700,
        '& .MuiChip-label': {
          px: size === 'small' ? 1.5 : 1.75,
        },
      }}
    />
  );

  useEffect(() => {
    const raw = postalCode || '';
    const query = raw.replace(/\D/g, ''); // Keep only numbers
    
    // Check for Brazilian CEP length (8 digits)
    if (query.length === 8 && query !== lastCepFetchedRef.current) {
      const timeoutId = setTimeout(() => {
        setCepLoading(true);
        setCepError(null);
        
        // Use ViaCEP API directly
        fetch(`https://viacep.com.br/ws/${query}/json/`)
          .then((res) => res.json())
          .then((data) => {
            if (data.erro) {
              setCepError('CEP não encontrado');
              showError('CEP não encontrado');
              return;
            }

            // Map ViaCEP fields to our form
            setAddressStreet(data.logradouro || '');
            setAddressDistrict(data.bairro || '');
            setAddressCity(data.localidade || '');
            setAddressState(data.uf || '');
            
            lastCepFetchedRef.current = query;
          })
          .catch((err) => {
            console.error('ViaCEP fetch error:', err);
            setCepError('Falha ao buscar endereço');
            showError('Falha ao buscar endereço');
          })
          .finally(() => setCepLoading(false));
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [postalCode]);

  useEffect(() => {
    if (authState.hasActivePlan) {
      bypassNextNavigationRef.current = true;
      navigate('/dashboard', { replace: true });
    }
  }, [authState.hasActivePlan, navigate]);

  const handleSubscribe = async () => {
    setLoading(true);
    setMessage(null);
    setError(null);
    setBoletoDetails(null);
    setHasResumedPendingBoleto(false);
    try {
      const hasBasicData = Boolean(String(name || '').trim() && String(cpfCnpj || '').trim() && String(email || '').trim());
      if (!hasBasicData) {
        const msg = t('payment.fill_all_fields');
        setError(msg);
        showError(msg);
        setLoading(false);
        return;
      }

      const digitsCpfCnpj = cpfCnpj.replace(/\D/g, '');
      if (digitsCpfCnpj.length === 11 && !isValidCPF(cpfCnpj)) {
        const msg = t('payment.invalid_cpf');
        setError(msg);
        showError(msg);
        setLoading(false);
        return;
      }

      const missingCreditCardFields =
        !String(cardHolderName || '').trim() ||
        !String(cardNumber || '').replace(/\D/g, '').trim() ||
        !String(expiryMonth || '').trim() ||
        !String(expiryYear || '').trim() ||
        !String(ccv || '').trim() ||
        !String(postalCode || '').trim() ||
        !String(addressNumber || '').trim();

      if (paymentMethod === 'CREDIT_CARD' && missingCreditCardFields) {
        const msg = t('payment.fill_card_fields');
        setError(msg);
        showError(msg);
        setLoading(false);
        return;
      }

      const accessBefore = await asaasService.checkAccessByEmail(email, getAccessCheckOptions());
      const reasonsBefore = Array.isArray((accessBefore as any)?.reasons) ? (accessBefore as any).reasons : [];
      const hasValidSubscriptionBefore = reasonsBefore.includes('subscription_valid');
      const accessGrantedBefore = Boolean((accessBefore as any)?.accessGranted);
      const existingSubBefore = hasValidSubscriptionBefore ? (accessBefore as any)?.subscription : null;

      if (existingSubBefore?.id && !accessGrantedBefore) {
        const paymentsResp = await asaasService.listSubscriptionPayments(String(existingSubBefore.id));
        const paymentItems = normalizePaymentsList(paymentsResp);
        const pendingBoletoPayment =
          paymentItems.find((p: any) => String(p?.billingType || '').toUpperCase() === 'BOLETO' && String(p?.status || '').toUpperCase() === 'PENDING') ||
          paymentItems.find((p: any) => String(p?.billingType || '').toUpperCase() === 'BOLETO') ||
          null;
        const invoiceUrl = getBoletoInvoiceUrl(pendingBoletoPayment);
        if (invoiceUrl) {
          const resumedCoupon = getCouponInfoFromPayment(pendingBoletoPayment);
          const resumedValue = Number(pendingBoletoPayment?.value);
          if (resumedCoupon) {
            setCouponInfo(resumedCoupon);
            setCouponAppliedForEmail(String(email || '').trim().toLowerCase());
            setCouponCodeInput(resumedCoupon.code);
          } else {
            setCouponInfo(null);
            setCouponAppliedForEmail('');
            setCouponCodeInput('');
          }
          if (Number.isFinite(resumedValue) && resumedValue >= 0) {
            setPlanPriceValue(resumedValue);
            setPlanPrice(resumedValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
          }
          setPaymentMethod('BOLETO');
          setBoletoDetails({
            invoiceUrl,
            dueDate: pendingBoletoPayment?.dueDate || null,
            value: typeof pendingBoletoPayment?.value === 'number' ? pendingBoletoPayment.value : null,
          });
          setHasResumedPendingBoleto(true);
          setShowPostSubscriptionSuccess(true);
          setMessage(null);
          return;
        }
      }

      if (accessGrantedBefore || existingSubBefore) {
        setMessage(t('payment.error_active_subscription'));
        if (variant !== 'webview') {
          setTimeout(() => {
            bypassNextNavigationRef.current = true;
            navigate(authState.isAuthenticated ? '/dashboard' : '/auth?signup=false', { replace: true });
          }, 3000);
        }
        return;
      }

      const upsert = await asaasService.upsertCustomer({
        name,
        cpfCnpj,
        email,
        mobilePhone,
        postalCode,
        address: addressStreet,
        addressNumber,
        addressComplement: addressComplement || null,
        addressDistrict,
        phone: phone || null,
      });
      const customerId = (upsert as any).customer?.id || (upsert as any)?.id;
      if (!customerId) throw new Error('Falha ao obter ID do cliente');

      // Direct subscription creation with credit card data (no separate tokenization)
      const today = new Date().toISOString().slice(0, 10);
      const fullPlanValue = 30.0;
      const canonicalPlanLabel = PLAN_LABEL_PT[selectedPlan] || PLAN_LABEL_PT[DEFAULT_PAID_PLAN];
      
      const subscriptionPayload: any = {
        customer: customerId,
        billingType: paymentMethod,
        nextDueDate: today,
        value: fullPlanValue,
        cycle: 'MONTHLY',
        description: `Plano ${canonicalPlanLabel} MUU R$${Number(fullPlanValue).toFixed(0)}/mês`,
        planType: selectedPlan,
        remoteIp: defaultRemoteIp,
        email: email,
        couponCode: shouldApplyAutoTrialOnSubmit ? undefined : couponInfo?.code || undefined,
        autoTrialIntent: shouldApplyAutoTrialOnSubmit || undefined,
        autoTrialAllowCreate: (shouldApplyAutoTrialOnSubmit && autoTrialCreationRequired) || undefined,
      };
      if (paymentMethod === 'CREDIT_CARD') {
        subscriptionPayload.creditCard = {
          holderName: cardHolderName || name,
          number: cardNumber.replace(/\D/g, ''),
          expiryMonth,
          expiryYear,
          ccv,
        };
        subscriptionPayload.creditCardHolderInfo = {
          name,
          email,
          cpfCnpj,
          postalCode,
          addressNumber,
          addressComplement: addressComplement || null,
          phone,
          mobilePhone,
        };
      }
      const sub = await asaasService.createSubscription(subscriptionPayload, { token: registrationToken || undefined });

      setMessage(t('payment.subscription_created_verifying'));

      if ((sub as any)?.deferredByCoupon) {
        const effectiveExpiry = String((sub as any)?.effectiveExpiry || '').trim();
        const formattedExpiry = effectiveExpiry ? new Date(effectiveExpiry).toLocaleDateString(locale) : '';
        setBoletoDetails(null);
        setHasResumedPendingBoleto(false);
        setMessage(
          t('payment.coupon_boleto_deferred_desc', {
            date: formattedExpiry || '—',
          }),
        );
        setShowPostSubscriptionSuccess(true);
        return;
      }

      if (paymentMethod === 'BOLETO') {
        const subscriptionId = String((sub as any)?.id || (sub as any)?.subscription?.id || '').trim();
        if (!subscriptionId) {
          throw new Error(t('payment.boleto_subscription_missing_id'));
        }
        const paymentsResp = await asaasService.listSubscriptionPayments(subscriptionId);
        const paymentItems = normalizePaymentsList(paymentsResp);
        const pendingBoletoPayment =
          paymentItems.find((p: any) => String(p?.billingType || '').toUpperCase() === 'BOLETO' && String(p?.status || '').toUpperCase() === 'PENDING') ||
          paymentItems.find((p: any) => String(p?.billingType || '').toUpperCase() === 'BOLETO') ||
          paymentItems.find((p: any) => String(p?.status || '').toUpperCase() === 'PENDING') ||
          paymentItems[0];
        const invoiceUrl = getBoletoInvoiceUrl(pendingBoletoPayment);
        if (!invoiceUrl) {
          throw new Error(t('payment.boleto_url_not_found'));
        }
        const resumedCoupon = getCouponInfoFromPayment(pendingBoletoPayment);
        const resumedValue = Number(pendingBoletoPayment?.value);
        if (resumedCoupon) {
          setCouponInfo(resumedCoupon);
          setCouponAppliedForEmail(String(email || '').trim().toLowerCase());
          setCouponCodeInput(resumedCoupon.code);
        } else {
          setCouponInfo(null);
          setCouponAppliedForEmail('');
          setCouponCodeInput('');
        }
        if (Number.isFinite(resumedValue) && resumedValue >= 0) {
          setPlanPriceValue(resumedValue);
          setPlanPrice(resumedValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        }
        setBoletoDetails({
          invoiceUrl,
          dueDate: pendingBoletoPayment?.dueDate || null,
          value: typeof pendingBoletoPayment?.value === 'number' ? pendingBoletoPayment.value : null,
        });
        setHasResumedPendingBoleto(false);
        setShowPostSubscriptionSuccess(true);
        setMessage(null);
        return;
      }
      
      let granted = false;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        const access = await asaasService.checkAccessByEmail(email, { token: registrationToken || undefined });
        granted = Boolean((access as any)?.accessGranted);
        if (granted) break;
        if (attempt < 3) {
          await delay(900);
        }
      }

      let hasAuthenticatedSession = authState.isAuthenticated;
      if (variant !== 'webview' && !hasAuthenticatedSession && granted && registrationEmail && registrationPassword) {
        const loginResult = await login({
          email: registrationEmail,
          password: registrationPassword,
          rememberMe: false,
        });
        hasAuthenticatedSession = Boolean(loginResult?.success);
      }

      if (hasAuthenticatedSession && granted) {
        if (typeof refreshPlanStatus === 'function') await refreshPlanStatus();
        bypassNextNavigationRef.current = true;
        navigate('/dashboard', { replace: true });
      } else {
        setShowPostSubscriptionSuccess(true);
        setMessage(null);
      }
    } catch (e: any) {
      const status = Number(e?.status || 0);
      const code = String(e?.data?.code || '');
      const errors = e?.data?.errors || [];

      Sentry.setTag('asaas_status', String(status));
      Sentry.setTag('asaas_error_code', code);
      Sentry.setTag('plan_type', selectedPlan);
      Sentry.setTag('billing_type', paymentMethod);
      Sentry.setTag('has_coupon', String(!!couponInfo));
      if (couponInfo) {
        Sentry.setTag('coupon_code', couponInfo.code);
        Sentry.setTag('coupon_discount_percent', String(couponInfo.discount));
        Sentry.setTag('is_full_discount', String(couponInfo.discount === 100));
      }
      Sentry.setTag('had_existing_access', 'unknown');
      if (registrationEmail) {
        Sentry.setUser({ email: registrationEmail });
      }
      if (errors.length > 0) {
        Sentry.setExtra('asaas_errors', errors.map((err: any) => ({
          code: err.code,
          description: err.description,
        })));
      }
      Sentry.setExtra('form_fields_filled', {
        name: !!name,
        email: !!email,
        cpfCnpj: !!cpfCnpj,
        cardNumber: !!cardNumber,
        postalCode: !!postalCode,
        addressNumber: !!addressNumber,
      });
      Sentry.captureException(e);

      const isCpfCnpjEmailConflict =
        status === 409 && code === 'CPF_CNPJ_ALREADY_LINKED_TO_ANOTHER_EMAIL';
      const isActiveSubscriptionConflict =
        status === 409 || code === 'ACTIVE_SUBSCRIPTION_EXISTS';
      if (isCpfCnpjEmailConflict) {
        const msg = t(
            'payment.error_cpf_cnpj_already_linked_email',
            'Este CPF/CNPJ ja esta vinculado a outro e-mail. Entre com o e-mail original ou fale com o suporte.'
          );
        setError(msg);
        showError(msg);
      } else if (isActiveSubscriptionConflict) {
        const msg = t('payment.error_active_subscription_login_hint');
        setError(msg);
        showError(msg);
        if (variant !== 'webview') {
          setTimeout(() => {
            bypassNextNavigationRef.current = true;
            navigate(authState.isAuthenticated ? '/dashboard' : '/auth?signup=false', { replace: true });
          }, 2500);
        }
      } else {
        const msg = getSubscriptionErrorMessage(e);
        setError(msg);
        showError(e); // Let the errorHandler extract the correct message
      }
    } finally {
      setLoading(false);
    }
  };

  const isStep1Valid = paymentMethod === 'BOLETO'
      ? Boolean(name && cpfCnpj && email && mobilePhone)
      : Boolean(name && cpfCnpj && email && postalCode && addressNumber);

  const renderCardVisual = () => (
    <Box sx={{ position: 'relative', perspective: '1000px', width: '100%', maxWidth: '340px', mx: 'auto' }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.brand.primary} 0%, ${DESIGN_TOKENS.colors.brand.primaryVariant} 100%)`,
        borderRadius: '16px',
        pt: '56.25%',
        position: 'relative',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        color: '#fff',
        overflow: 'hidden'
      }}>
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
        
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
             <img src="/logo.svg" alt="Muu" style={{ height: 24, filter: 'brightness(0) invert(1)' }} />
             <Wifi sx={{ opacity: 0.8 }} />
          </Box>
          
          <Box sx={{ mt: 2 }}>
             <Box sx={{ width: 40, height: 30, background: '#E0E0E0', borderRadius: 1, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 24, height: 18, border: '1px solid #999', borderRadius: 0.5 }} />
             </Box>
             <Typography sx={{ fontFamily: 'monospace', fontSize: '1.2rem', letterSpacing: 2, textShadow: '0 2px 4px rgba(0,0,0,0.3)', color: '#FFFFFF' }}>
               {cardNumber || '•••• •••• •••• ••••'}
             </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
             <Box>
               <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.6rem', textTransform: 'uppercase', color: '#FFFFFF' }}>{t('payment.card_holder_label_short')}</Typography>
               <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', color: '#FFFFFF' }}>{cardHolderName || t('payment.card_holder_name_placeholder_short')}</Typography>
             </Box>
             <Box>
               <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.6rem', textTransform: 'uppercase', color: '#FFFFFF' }}>{t('payment.card_expiry_label_short')}</Typography>
               <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF' }}>{expiryMonth || t('payment.card_expiry_mm')}/{expiryYear || t('payment.card_expiry_yy')}</Typography>
             </Box>
             <Box sx={{ display: 'flex', alignItems: 'center' }}>
               <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.8)', mr: -1.5, zIndex: 1 }} />
               <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.5)' }} />
             </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const renderPostSubscriptionSuccess = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2 }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: `${DESIGN_TOKENS.colors.status.success[500]}1A`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <CheckCircle sx={{ fontSize: 44, color: DESIGN_TOKENS.colors.status.success[500] }} />
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.text.primary, mb: 1 }}>
        {t('payment.success_title')}
      </Typography>
      <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, maxWidth: 360 }}>
        {message || t('payment.success_desc')}
      </Typography>

      <Box sx={{ width: '100%', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={async () => {
            await logout();
            bypassNextNavigationRef.current = true;
            navigate('/auth?signup=false', { replace: true });
          }}
          sx={{
            width: '100%',
            height: 56,
            fontSize: '1.05rem',
            fontWeight: 700,
            bgcolor: DESIGN_TOKENS.colors.brand.primary,
            '&:hover': { bgcolor: DESIGN_TOKENS.colors.brand.primaryVariant },
            boxShadow: '0 10px 20px rgba(0,78,100,0.2)',
          }}
        >
          {t('payment.enter_account')}
        </Button>
      </Box>
    </Box>
  );

  const renderBoletoSuccess = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2 }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: `${DESIGN_TOKENS.colors.status.success[500]}1A`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <ReceiptLong sx={{ fontSize: 40, color: DESIGN_TOKENS.colors.status.success[500] }} />
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.text.primary, mb: 1 }}>
        {hasResumedPendingBoleto ? t('payment.boleto_resumed_title') : t('payment.boleto_generated_title')}
      </Typography>
      <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, maxWidth: 380 }}>
        {hasResumedPendingBoleto ? t('payment.boleto_resumed_desc') : t('payment.boleto_generated_desc')}
      </Typography>

      {boletoDetails?.dueDate && (
        <Typography variant="body2" sx={{ mt: 2, color: DESIGN_TOKENS.colors.text.secondary }}>
          {t('payment.boleto_due_date')}{' '}
          <Box component="span" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
            {new Date(String(boletoDetails.dueDate)).toLocaleDateString(locale)}
          </Box>
        </Typography>
      )}
      {typeof boletoDetails?.value === 'number' && (
        <Typography variant="body2" sx={{ mt: 0.5, color: DESIGN_TOKENS.colors.text.secondary }}>
          {t('payment.boleto_amount')}{' '}
          <Box component="span" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
            {Number(boletoDetails.value).toLocaleString(locale, { style: 'currency', currency: 'BRL' })}
          </Box>
        </Typography>
      )}

      <Box sx={{ width: '100%', mt: 3 }}>
        <Stack spacing={1.5}>
          <Button
            variant="contained"
            size="large"
            onClick={() => window.open(String(boletoDetails?.invoiceUrl || ''), '_blank', 'noopener,noreferrer')}
            sx={{
              width: '100%',
              height: 56,
              fontSize: '1.05rem',
              fontWeight: 700,
              bgcolor: DESIGN_TOKENS.colors.brand.primary,
              '&:hover': { bgcolor: DESIGN_TOKENS.colors.brand.primaryVariant },
              boxShadow: '0 10px 20px rgba(0,78,100,0.2)',
            }}
            endIcon={<OpenInNew />}
          >
            {t('payment.open_boleto')}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={async () => {
              await logout();
              bypassNextNavigationRef.current = true;
              navigate('/auth?signup=false', { replace: true });
            }}
            sx={{ width: '100%', height: 52, fontWeight: 700 }}
          >
            {t('payment.enter_account')}
          </Button>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: DESIGN_TOKENS.colors.background.default,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {variant !== 'webview' && (
        <Box sx={{ 
          width: '100%', 
          bgcolor: '#fff', 
          borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
          py: 2,
          px: { xs: 2, md: 4 }
        }}>
          <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <img src="/logo.svg" alt="MUU" style={{ height: 32 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: DESIGN_TOKENS.colors.status.success[500] }}>
              <Lock fontSize="small" />
              <Typography variant="body2" fontWeight={600}>{t('payment.secure_payment')}</Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ 
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
        mt: variant === 'webview' ? 0 : { xs: 0, md: 2 }
      }}>
        <Card sx={{ 
          width: '100%', 
          maxWidth: variant === 'webview' ? 520 : 1000, 
          minHeight: variant === 'webview' ? 'auto' : 600,
          borderRadius: 4, 
          boxShadow: '0 24px 48px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: variant === 'webview' ? 'column' : { xs: 'column-reverse', md: 'row' },
          overflow: 'hidden'
        }}>
          
          <Box sx={{ 
            flex: 1.2, 
            p: variant === 'webview' ? { xs: 3, md: 4 } : { xs: 3, md: 5 },
            display: 'flex',
            flexDirection: 'column'
          }}>
            {!showPostSubscriptionSuccess && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="text"
                  startIcon={<ArrowBack />}
                  onClick={handleBackNavigation}
                  disabled={loading}
                  sx={{ pl: 0, minWidth: 'auto' }}
                >
                  {activeStep === 1 ? t('buttons.back') : t('buttons.back_to_plans')}
                </Button>
              </Box>
            )}
            {/* Custom Loading */}
            {loading && <Loading message="" fullScreen={true} />}

            {showPostSubscriptionSuccess ? (
              boletoDetails ? renderBoletoSuccess() : renderPostSubscriptionSuccess()
            ) : (
              <Box sx={{ mb: 4, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexWrap: 'wrap', mb: 0.75 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
                      {shouldDisplayAutoTrial && activeStep === 0
                        ? t('payment.auto_trial_header_title')
                        : activeStep === 0
                          ? t('payment.personal_data')
                          : t('payment.payment_data')}
                    </Typography>
                    {shouldDisplayAutoTrial && activeStep === 0 && renderAutoTrialChip()}
                  </Stack>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, maxWidth: 460 }}>
                    {shouldDisplayAutoTrial && activeStep === 0
                      ? t('payment.auto_trial_header_desc', { date: autoTrialDisplayDate || '—' })
                      : activeStep === 0
                        ? t('payment.personal_data_desc')
                        : paymentMethod === 'BOLETO'
                          ? t('payment.payment_data_desc_boleto')
                          : t('payment.payment_data_desc')}
                  </Typography>
                  {autoTrialLoading && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: DESIGN_TOKENS.colors.text.tertiary }}>
                      {t('payment.auto_trial_loading')}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

          <Fade in={!showPostSubscriptionSuccess && activeStep === 0} unmountOnExit>
            <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
              <Stack spacing={2.5}>
                {isAutoTrialBlocking && autoTrialError && (
                  <Alert
                    severity="error"
                    action={
                      <Button color="inherit" size="small" onClick={retryAutoTrialBootstrap}>
                        {t('payment.auto_trial_retry')}
                      </Button>
                    }
                  >
                    {autoTrialError}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label={t('forms:register.name')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  InputProps={{ startAdornment: <InputAdornment position="start"><Person color="action" /></InputAdornment> }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                    fullWidth
                    label={t('payment.cpf_cnpj_label')}
                    value={cpfCnpj}
                    onChange={(e) => setCpfCnpj(formatCpfCnpj(e.target.value))}
                    required
                    inputProps={{ maxLength: 18 }}
                    />
                    <TextField
                    fullWidth
                    label={t('forms:farm.contact_info.phone.label')}
                    value={mobilePhone}
                    onChange={(e) => setMobilePhone(formatPhone(e.target.value))}
                    required
                    placeholder={t('forms:farm.contact_info.phone.placeholder')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Phone color="action" /></InputAdornment> }}
                    inputProps={{ maxLength: 15 }}
                    />
                </Stack>
                <TextField
                  fullWidth
                  label={t('forms:register.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => {
                    if (variant !== 'webview') checkSubscription(email);
                  }}
                  required
                  disabled={isEmailFixed}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Email color="action" /></InputAdornment> }}
                />

                <FormControl component="fieldset">
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}>
                    {t('payment.payment_method')}
                  </Typography>
                  <RadioGroup
                    row
                    value={paymentMethod}
                    onChange={(event) => setPaymentMethod(event.target.value as PaymentMethod)}
                  >
                    <FormControlLabel value="CREDIT_CARD" control={<Radio />} label={t('payment.method_credit_card')} />
                    <FormControlLabel value="BOLETO" control={<Radio />} label={t('payment.method_boleto')} />
                  </RadioGroup>
                </FormControl>
                
                {couponInfo && !shouldDisplayAutoTrial && (
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${DESIGN_TOKENS.colors.brand.primary}`,
                      bgcolor: `${DESIGN_TOKENS.colors.brand.primary}14`,
                      p: 2.25,
                    }}
                  >
                    <Stack spacing={1}>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: DESIGN_TOKENS.colors.brand.primary }}>
                        {shouldDisplayAutoTrial ? t('payment.auto_trial_card_title') : t('payment.coupon_access_title')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, lineHeight: 1.6 }}>
                        {shouldDisplayAutoTrial
                          ? t('payment.auto_trial_card_desc', { date: autoTrialDisplayDate || '—' })
                          : isFullDiscountCoupon
                          ? t('payment.coupon_access_desc_full')
                          : t('payment.coupon_access_desc_percent', { discount: couponInfo.discount })}
                      </Typography>
                      {!shouldDisplayAutoTrial && (
                        <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                          {t('payment.coupon_code_line', { code: couponInfo.code })}
                        </Typography>
                      )}
                      {couponInfo?.expirationDate && (
                        <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                          {t('payment.voucher_until')}{' '}
                          <Box component="span" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.text.primary }}>
                            {new Date(couponInfo.expirationDate).toLocaleDateString(locale)}
                          </Box>
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                        {t('payment.voucher_cancel_anytime')}
                      </Typography>
                    </Stack>
                  </Box>
                )}
                {paymentMethod === 'CREDIT_CARD' ? (
                  <>
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('payment.address.street').toUpperCase()}
                      </Typography>
                    </Divider>
                    
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label={t('payment.address.zip')}
                        value={postalCode}
                        onChange={(e) => setPostalCode(formatCep(e.target.value))}
                        required
                        sx={{ width: '220px' }}
                        inputProps={{ maxLength: 9 }}
                        InputProps={{
                          endAdornment: cepLoading ? (
                            <InputAdornment position="end">
                              <CircularProgress size={20} color="inherit" />
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                      <TextField
                        fullWidth
                        label={t('payment.address.street')}
                        value={addressStreet}
                        onChange={(e) => setAddressStreet(e.target.value)}
                        disabled={cepLoading}
                      />
                    </Stack>
                    
                    <Stack direction="row" spacing={2}>
                      <TextField
                        label={t('payment.address.number')}
                        value={addressNumber}
                        onChange={(e) => setAddressNumber(e.target.value)}
                        required
                        sx={{ width: '140px' }}
                      />
                      <TextField
                        fullWidth
                        label={t('payment.address.district')}
                        value={addressDistrict}
                        onChange={(e) => setAddressDistrict(e.target.value)}
                      />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        fullWidth
                        label={t('payment.address.city')}
                        value={addressCity}
                        onChange={(e) => setAddressCity(e.target.value)}
                      />
                      <TextField
                        label={t('payment.address.state')}
                        value={addressState}
                        onChange={(e) => setAddressState(e.target.value)}
                        sx={{ width: '120px' }}
                      />
                    </Stack>
                  </>
                ) : (
                  <Alert severity="info">
                    {t('payment.boleto_step1_hint')}
                  </Alert>
                )}

                {message && <Alert severity="info" sx={{ mt: 2 }}>{message}</Alert>}

                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={() => setActiveStep(1)}
                      disabled={!isStep1Valid || autoTrialLoading || isAutoTrialBlocking}
                  sx={{ 
                    mt: 2, 
                    height: shouldDisplayAutoTrial ? 58 : 48,
                    bgcolor: DESIGN_TOKENS.colors.brand.primary,
                    '&:hover': { bgcolor: DESIGN_TOKENS.colors.brand.primaryVariant }
                  }}
                    >
                      {shouldDisplayAutoTrial ? (
                        <Box sx={{ textAlign: 'center', lineHeight: 1.1 }}>
                          <Typography component="span" sx={{ display: 'block', fontWeight: 700, fontSize: '1rem', color: '#fff' }}>
                            {t('payment.auto_trial_cta')}
                          </Typography>
                          <Typography component="span" sx={{ display: 'block', fontWeight: 500, fontSize: '0.75rem', color: 'rgba(255,255,255,0.82)', mt: 0.25 }}>
                            {t('payment.auto_trial_cta_hint')}
                          </Typography>
                        </Box>
                      ) : t('payment.continue_payment')}
                    </Button>
                    {shouldDisplayAutoTrial && (
                      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ mt: 0.5, color: DESIGN_TOKENS.colors.text.tertiary }}>
                        <Lock sx={{ fontSize: 14 }} />
                        <Typography variant="caption">
                          {t('payment.auto_trial_footer_note', { date: autoTrialDisplayDate || '—' })}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </Fade>

          <Fade in={!showPostSubscriptionSuccess && activeStep === 1} unmountOnExit>
             <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
                <Stack spacing={3}>
                    {variant === 'webview' && (
                      <Box>
                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                          {paymentMethod === 'CREDIT_CARD' ? (
                            renderCardVisual()
                          ) : (
                            <Box sx={{ p: 3, borderRadius: 3, border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`, bgcolor: '#fff' }}>
                              <ReceiptLong sx={{ fontSize: 48, color: DESIGN_TOKENS.colors.brand.primary }} />
                              <Typography sx={{ mt: 1.5, fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
                                {t('payment.method_boleto')}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                              color: DESIGN_TOKENS.colors.status.success[500],
                              bgcolor: 'rgba(255,255,255,0.8)',
                              px: 2,
                              py: 0.75,
                              borderRadius: 2,
                              border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                            }}
                          >
                            <Security fontSize="small" />
                            <Typography variant="caption" fontWeight={700}>{t('payment.secure_payment')}</Typography>
                          </Stack>

                          <Stack direction="row" spacing={1} alignItems="center">
                            <Lock fontSize="small" sx={{ fontSize: 14, color: DESIGN_TOKENS.colors.text.tertiary }} />
                            <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.tertiary }}>
                              {t('payment.processed_by')}
                            </Typography>
                          </Stack>

                          <Box sx={{ mt: 0.5, display: 'flex', gap: 2, opacity: 0.6 }}>
                            <Box sx={{ width: 32, height: 20, bgcolor: '#e0e0e0', borderRadius: 0.5 }} />
                            <Box sx={{ width: 32, height: 20, bgcolor: '#e0e0e0', borderRadius: 0.5 }} />
                            <Box sx={{ width: 32, height: 20, bgcolor: '#e0e0e0', borderRadius: 0.5 }} />
                          </Box>

                          <Box sx={{ mt: 1, width: '100%', border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`, borderRadius: 3, p: 2, bgcolor: '#fff' }}>
                            {shouldDisplayAutoTrial && (
                              <Box sx={{ mb: 2 }}>
                                {renderAutoTrialChip()}
                              </Box>
                            )}
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                                  {t('payment.plan')}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
                                  Muu {planLabel}
                                </Typography>
                              </Box>
                              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                <InputLabel>{t('payment.change_plan')}</InputLabel>
                                <Select
                                  label={t('payment.change_plan')}
                                  value={selectedPlan}
                                  onChange={handlePlanChange}
                                >
                                  <MenuItem value="producer">{t('landing:pricing.plans.producer.name')}</MenuItem>
                                  <MenuItem value="professional">{t('landing:pricing.plans.professional.name')}</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            {shouldDisplayAutoTrial ? (
                              <Stack spacing={1.5} sx={{ mt: 2 }}>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
                                      {t('payment.auto_trial_summary_today_label')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.tertiary }}>
                                      {t('payment.auto_trial_summary_today_note')}
                                    </Typography>
                                  </Box>
                                  <Typography variant="h6" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.status.success[500] }}>
                                    {t('payment.auto_trial_summary_today_price')}
                                  </Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
                                      {t('payment.auto_trial_summary_after_label', { date: autoTrialDisplayDate || '—' })}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.tertiary }}>
                                      {t('payment.auto_trial_summary_after_note')}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body1" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.text.primary }}>
                                    {autoTrialSummaryPrice}
                                  </Typography>
                                </Box>
                              </Stack>
                            ) : (
                              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                                  {t('payment.total')}
                                </Typography>
                                {couponInfo ? (
                                  <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.tertiary, textDecoration: 'line-through', display: 'block' }}>
                                      R$ 30,00
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 900, color: DESIGN_TOKENS.colors.brand.primary }}>
                                      R$ {planPrice}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.status.success[500], display: 'block' }}>
                                      {`${couponInfo.discount}% OFF (${couponInfo.code})`}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="h6" sx={{ fontWeight: 900, color: DESIGN_TOKENS.colors.brand.primary }}>
                                    R$ 30,00
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    {variant === 'webview' && (
                      <Box sx={{ width: '100%' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField
                            fullWidth
                            label={t('payment.coupon_code', 'Cupom')}
                            value={couponCodeInput}
                            onChange={handleCouponInputChange}
                            disabled={couponLoading || isCouponInputLocked}
                            error={Boolean(couponError)}
                            helperText={couponError || undefined}
                            InputProps={{
                              endAdornment: !isCouponInputLocked && (couponCodeInput.trim() || couponInfo) ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={t('payment.remove_coupon', 'Remover cupom')}
                                    onClick={clearCoupon}
                                    disabled={couponLoading}
                                    size="small"
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : undefined,
                            }}
                          />
                          <Button
                            variant="outlined"
                            onClick={applyCouponCode}
                            disabled={couponLoading || isCouponInputLocked || !couponCodeInput.trim()}
                            sx={{ height: 56, minWidth: 120 }}
                          >
                            {couponLoading ? t('payment.processing', 'Processando...') : t('payment.apply_coupon', 'Aplicar')}
                          </Button>
                        </Stack>
                        {couponInfo && !shouldDisplayAutoTrial && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: DESIGN_TOKENS.colors.status.success[500], fontWeight: 700 }}>
                            {couponInfo.discount}% OFF ({couponInfo.code})
                          </Typography>
                        )}
                      </Box>
                    )}

                    {couponInfo && !shouldDisplayAutoTrial && (
                      <Box
                        sx={{
                          borderRadius: 3,
                          border: `1px solid ${DESIGN_TOKENS.colors.brand.primary}`,
                          bgcolor: `${DESIGN_TOKENS.colors.brand.primary}14`,
                          p: 2.25,
                        }}
                      >
                        <Stack spacing={1}>
                          <Typography variant="h6" sx={{ fontWeight: 900, color: DESIGN_TOKENS.colors.brand.primary }}>
                            {shouldDisplayAutoTrial ? t('payment.auto_trial_card_title') : t('payment.coupon_access_title')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, lineHeight: 1.6 }}>
                            {shouldDisplayAutoTrial
                              ? t('payment.auto_trial_card_desc', { date: autoTrialDisplayDate || '—' })
                              : isFullDiscountCoupon
                              ? t('payment.coupon_access_desc_full')
                              : t('payment.coupon_access_desc_percent', { discount: couponInfo.discount })}
                          </Typography>
                          {!shouldDisplayAutoTrial && (
                            <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                              {t('payment.coupon_code_line', { code: couponInfo.code })}
                            </Typography>
                          )}
                          {couponInfo?.expirationDate && (
                            <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                              {t('payment.voucher_until')}{' '}
                              <Box component="span" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.text.primary }}>
                                {new Date(couponInfo.expirationDate).toLocaleDateString(locale)}
                              </Box>
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                            {t('payment.voucher_cancel_anytime')}
                          </Typography>
                        </Stack>
                      </Box>
                    )}

                    <>
                    <FormControl component="fieldset">
                        <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}>
                          {t('payment.payment_method')}
                        </Typography>
                        <RadioGroup
                          row
                          value={paymentMethod}
                          onChange={(event) => setPaymentMethod(event.target.value as PaymentMethod)}
                        >
                          <FormControlLabel value="CREDIT_CARD" control={<Radio />} label={t('payment.method_credit_card')} />
                          <FormControlLabel value="BOLETO" control={<Radio />} label={t('payment.method_boleto')} />
                        </RadioGroup>
                    </FormControl>
                    {paymentMethod === 'BOLETO' ? (
                      <Alert severity="info">
                        {t('payment.boleto_generation_hint')}
                      </Alert>
                    ) : (
                      <>
                    <TextField
                        fullWidth
                        label={t('payment.card_number')}
                        value={cardNumber}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            const truncated = value.slice(0, 16);
                            const formatted = truncated.replace(/(\d{4})(?=\d)/g, '$1 ');
                            setCardNumber(formatted);
                        }}
                        required
                        InputProps={{ 
                            startAdornment: <InputAdornment position="start"><CreditCardIcon color="action" /></InputAdornment>,
                            endAdornment: <InputAdornment position="end"><CheckCircle color={cardNumber.replace(/\D/g, '').length >= 16 ? "success" : "disabled"} /></InputAdornment>
                        }}
                        inputProps={{ maxLength: 19 }}
                    />
                    
                    <TextField
                        fullWidth
                        label={t('payment.card_holder')}
                        value={cardHolderName}
                        onChange={(e) => setCardHolderName(e.target.value)}
                        required
                        placeholder={t('payment.card_holder_placeholder')}
                    />

                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, mb: 0.5, display: 'block' }}>{t('payment.expiry')}</Typography>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    placeholder={t('payment.card_expiry_mm')}
                                    value={expiryMonth}
                                    onChange={(e) => setExpiryMonth(e.target.value)}
                                    required
                                    inputProps={{ maxLength: 2 }}
                                />
                                <Typography sx={{ alignSelf: 'center', color: 'text.secondary' }}>/</Typography>
                                <TextField
                                    placeholder={t('payment.card_expiry_yy')}
                                    value={expiryYear}
                                    onChange={(e) => setExpiryYear(e.target.value)}
                                    required
                                    inputProps={{ maxLength: 2 }}
                                />
                            </Stack>
                        </Box>
                        <Box sx={{ width: '120px' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, mb: 0.5, display: 'block' }}>{t('payment.cvv')}</Typography>
                            <TextField
                                fullWidth
                                value={ccv}
                                onChange={(e) => setCcv(e.target.value)}
                                required
                                type="password"
                                InputProps={{ startAdornment: <InputAdornment position="start"><Lock fontSize="small" color="action" /></InputAdornment> }}
                            />
                        </Box>
                    </Stack>
                      </>
                    )}
                    </>

                    {message && <Alert severity="info">{message}</Alert>}
                    
                    <Button 
                        variant="contained" 
                        size="large"
                        onClick={handleSubscribe}
                        disabled={loading || autoTrialLoading || isAutoTrialBlocking}
                        sx={{ 
                            mt: 2, 
                            height: 56,
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            bgcolor: DESIGN_TOKENS.colors.brand.primary,
                            '&:hover': { bgcolor: DESIGN_TOKENS.colors.brand.primaryVariant },
                            boxShadow: '0 10px 20px rgba(0,78,100,0.2)'
                        }}
                    >
                      {/* Sempre mostra o texto estático pois o Loading fullScreen cobre a tela */}
                      {paymentMethod === 'BOLETO'
                        ? t('payment.generate_boleto')
                        : couponInfo?.discount === 100
                          ? t('payment.get_access')
                          : t('payment.pay_button', { amount: planPrice })}
                    </Button>
                </Stack>
             </Box>
          </Fade>
        </Box>

        {variant !== 'webview' && (
          <Box sx={{ 
            flex: 1, 
            bgcolor: DESIGN_TOKENS.colors.background.elevated,
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            borderLeft: { md: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` }
          }}>
              <Box sx={{ position: 'absolute', top: -100, right: -100, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,78,100,0.05) 0%, rgba(0,0,0,0) 70%)' }} />
              
              <Box sx={{ mb: 6, textAlign: 'center' }}>
                  {paymentMethod === 'CREDIT_CARD' ? (
                    renderCardVisual()
                  ) : (
                    <Box sx={{ py: 4 }}>
                      <ReceiptLong sx={{ fontSize: 64, color: DESIGN_TOKENS.colors.brand.primary }} />
                      <Typography sx={{ mt: 1.5, fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
                        {t('payment.method_boleto')}
                      </Typography>
                    </Box>
                  )}
              </Box>
  
              <Box sx={{ maxWidth: 360, mx: 'auto', width: '100%' }}>
                  {shouldDisplayAutoTrial && (
                    <Box sx={{ mb: 2.5 }}>
                      {renderAutoTrialChip()}
                    </Box>
                  )}
                  <Typography variant="subtitle2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('payment.summary_title')}
                  </Typography>
                  
                  <Stack spacing={2}>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                              {t('payment.plan')}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>Muu {planLabel}</Typography>
                        </Box>
                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                          <InputLabel>{t('payment.change_plan')}</InputLabel>
                          <Select
                            label={t('payment.change_plan')}
                            value={selectedPlan}
                            onChange={handlePlanChange}
                          >
                            <MenuItem value="producer">{t('landing:pricing.plans.producer.name')}</MenuItem>
                            <MenuItem value="professional">{t('landing:pricing.plans.professional.name')}</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                            {t('payment.frequency')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {t('payment.frequency_monthly')}
                          </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ width: '100%' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <TextField
                            fullWidth
                            label={t('payment.coupon_code', 'Cupom')}
                            value={couponCodeInput}
                            onChange={handleCouponInputChange}
                            disabled={couponLoading || isCouponInputLocked}
                            error={Boolean(couponError)}
                            helperText={couponError || undefined}
                            InputProps={{
                              endAdornment: !isCouponInputLocked && (couponCodeInput.trim() || couponInfo) ? (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={t('payment.remove_coupon', 'Remover cupom')}
                                    onClick={clearCoupon}
                                    disabled={couponLoading}
                                    size="small"
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ) : undefined,
                            }}
                          />
                          <Button
                            variant="outlined"
                            onClick={applyCouponCode}
                            disabled={couponLoading || isCouponInputLocked || !couponCodeInput.trim()}
                            sx={{ height: 56, minWidth: 120 }}
                          >
                            {couponLoading ? t('payment.processing', 'Processando...') : t('payment.apply_coupon', 'Aplicar')}
                          </Button>
                        </Stack>
                        {couponInfo && !shouldDisplayAutoTrial && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: DESIGN_TOKENS.colors.status.success[500], fontWeight: 700 }}>
                            {shouldDisplayAutoTrial
                              ? t('payment.auto_trial_badge')
                              : `${couponInfo.discount}% OFF (${couponInfo.code})`}
                          </Typography>
                        )}
                      {shouldDisplayAutoTrial && (
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                            <Box>
                              <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary, fontWeight: 700 }}>
                                {t('payment.auto_trial_summary_today_label')}
                              </Typography>
                              <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.tertiary }}>
                                {t('payment.auto_trial_summary_today_note')}
                              </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: DESIGN_TOKENS.colors.status.success[500], fontWeight: 800 }}>
                              {t('payment.auto_trial_summary_today_price')}
                            </Typography>
                          </Box>
                          <Divider />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                            <Box>
                              <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary, fontWeight: 700 }}>
                                {t('payment.auto_trial_summary_after_label', { date: autoTrialDisplayDate || '—' })}
                              </Typography>
                              <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.tertiary }}>
                                {t('payment.auto_trial_summary_after_note')}
                              </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary, fontWeight: 800, pt: 0.25 }}>
                              {autoTrialSummaryPrice}
                            </Typography>
                          </Box>
                        </Stack>
                      )}
                      {!shouldDisplayAutoTrial && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary, fontWeight: 700 }}>
                              {t('payment.total_to_pay')}
                            </Typography>
                            <Box sx={{ textAlign: 'right' }}>
                                {couponInfo ? (
                                  <>
                                    <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.tertiary, textDecoration: 'line-through' }}>R$ 30,00</Typography>
                                    <Typography variant="h4" sx={{ color: DESIGN_TOKENS.colors.brand.primary, fontWeight: 800 }}>R$ {planPrice}</Typography>
                                    <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.status.success[500], display: 'block' }}>
                                      {t('payment.discount_badge', { discount: couponInfo.discount, code: couponInfo.code })}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography variant="h4" sx={{ color: DESIGN_TOKENS.colors.brand.primary, fontWeight: 800 }}>R$ 30,00</Typography>
                                )}
                                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.tertiary }}>BRL</Typography>
                            </Box>
                        </Box>
                      )}
                      </Box>
                  </Stack>
                  
                  <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, opacity: 0.8 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: DESIGN_TOKENS.colors.status.success[500], bgcolor: 'rgba(255,255,255,0.6)', px: 2, py: 0.5, borderRadius: 2 }}>
                          <Security fontSize="small" />
                          <Typography variant="caption" fontWeight={600}>{t('payment.secure_payment')}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                          <Lock fontSize="small" sx={{ fontSize: 14, color: DESIGN_TOKENS.colors.text.tertiary }} />
                          <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.tertiary }}>{t('payment.processed_by')}</Typography>
                      </Stack>
                      <Box sx={{ mt: 1, display: 'flex', gap: 2, opacity: 0.6 }}>
                           <Box sx={{ width: 32, height: 20, bgcolor: '#e0e0e0', borderRadius: 0.5 }} />
                           <Box sx={{ width: 32, height: 20, bgcolor: '#e0e0e0', borderRadius: 0.5 }} />
                           <Box sx={{ width: 32, height: 20, bgcolor: '#e0e0e0', borderRadius: 0.5 }} />
                      </Box>
                  </Box>
              </Box>
            </Box>
        )}
        </Card>
      </Box>

    </Box>
  );
};

export default AsaasPaymentScreen;
